<script setup lang="ts">
import {
  BookOpenText,
  Cable,
  FlaskConical,
  History,
  KeyRound,
  LayoutDashboard,
  Menu,
  ShieldCheck,
  UserRound,
  WalletCards,
  X,
} from "lucide-vue-next"
import { computed, ref } from "vue"
import { useRoute } from "vue-router"

import { useSessionStore } from "../stores/session"

const route = useRoute()
const session = useSessionStore()
const menuOpen = ref(false)

const navigation = [
  { name: "概览", to: "/", icon: LayoutDashboard },
  { name: "接入 Agent", to: "/connect", icon: Cable },
  { name: "API Keys", to: "/api-keys", icon: KeyRound },
  { name: "调用与消耗", to: "/usage", icon: History },
  { name: "套餐与额度", to: "/billing", icon: WalletCards },
  { name: "Playground", to: "/playground", icon: FlaskConical },
  { name: "Quickstart", to: "/quickstart", icon: BookOpenText },
  { name: "账户", to: "/account", icon: UserRound },
]

const currentTitle = computed(
  () => navigation.find((item) => item.to === route.path)?.name ?? "ByreCC",
)

const userInitial = computed(() => {
  const value = session.me?.display_name || session.me?.email || "B"
  return value.trim().slice(0, 1).toUpperCase()
})

const userLabel = computed(
  () => session.me?.display_name || session.me?.email?.split("@")[0] || "账户",
)
</script>

<template>
  <div class="app-shell">
    <aside class="sidebar" :class="{ 'sidebar--open': menuOpen }">
      <div class="brand-row">
        <RouterLink to="/" class="brand" @click="menuOpen = false">
          <span class="brand__icon" aria-hidden="true"></span>
          <span class="brand__word">
            <strong>ByreCC</strong>
            <small>牛马看世界</small>
          </span>
        </RouterLink>
        <button class="icon-button mobile-only" aria-label="关闭菜单" @click="menuOpen = false">
          <X :size="18" />
        </button>
      </div>

      <div class="workspace-switcher">
        <span class="eyebrow">工作区</span>
        <strong>{{ session.workspace?.name ?? "正在加载" }}</strong>
        <span><i></i>{{ session.workspace?.role ?? "—" }} · {{ session.workspace?.plan_code ?? "free" }}</span>
      </div>

      <nav class="side-nav" aria-label="控制台导航">
        <RouterLink
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          class="side-nav__item"
          @click="menuOpen = false"
        >
          <component :is="item.icon" :size="17" stroke-width="1.8" />
          <span>{{ item.name }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar__foot">
        <RouterLink v-if="session.isSystemAdmin" to="/admin" class="admin-entry-link">
          <ShieldCheck :size="15" /> 系统管理
        </RouterLink>
        <RouterLink to="/account" class="sidebar-user" @click="menuOpen = false">
          <span class="sidebar-user__avatar">{{ userInitial }}</span>
          <span>
            <strong>{{ userLabel }}</strong>
            <small>{{ session.me?.email }}</small>
          </span>
        </RouterLink>
      </div>
    </aside>

    <div v-if="menuOpen" class="sidebar-scrim" @click="menuOpen = false"></div>

    <main class="main-column">
      <header class="topbar">
        <div class="topbar__title">
          <button class="icon-button mobile-only" aria-label="打开菜单" @click="menuOpen = true">
            <Menu :size="19" />
          </button>
          <span class="topbar__path">{{ currentTitle }}</span>
        </div>
      </header>
      <div class="page-frame">
        <RouterView />
      </div>
    </main>
  </div>
</template>
